// react
import { MouseEvent, ReactNode } from 'react';

// types
import { ActionButton } from '../../../@types';

// mui
import {
	Button,
	Dialog,
	IconButton,
	Grid,
	Typography,
	TypographyProps,
	DialogProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// ui
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// icons
import { ClearOutlined as IconClear } from '@mui/icons-material';

// props
export interface DialogActionsProps extends Omit<
	DialogProps,
	'content' | 'maxWidth' | 'open' | 'title'
> {
	actions?: ActionButton[];
	alignItems?: CSSStyleDeclaration['alignItems'];
	children?: ReactNode;
	content?: ReactNode;
	hasClose?: boolean;
	isOpen: boolean;
	maxWidth?: number;
	onClose: (e: MouseEvent<HTMLButtonElement>) => void;
	title?: TypographyProps;
	subtitle?: TypographyProps;
	type?: IconCircleProps['type'];
	fullScreen?: boolean;
}

export const DialogActions = ({
	actions = [],
	alignItems = 'flex-start',
	children,
	content,
	hasClose = false,
	isOpen = false,
	maxWidth = 500,
	onClose,
	PaperProps,
	title,
	subtitle,
	type,
	fullScreen,
}: DialogActionsProps) => {
	const renderTitle = () => {
		if (!title) {
			return false;
		}

		const { children, fontWeight, variant, ...titleProps } = title;

		return (
			<Typography
				fontWeight={fontWeight || 600}
				id="dialog-actions-title"
				variant={variant || 'h2'}
				{...titleProps}>
				{children}
			</Typography>
		);
	};

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			aria-labelledby="dialog-actions-title"
			aria-describedby="dialog-actions-description"
			maxWidth="md"
			fullScreen={fullScreen}
			PaperProps={{
				sx: (theme) => {
					return {
						padding: {
							xs: theme.spacing(2),
							md: theme.spacing(4),
						},
						maxWidth: fullScreen ? null : maxWidth,
						...(PaperProps?.sx as object),
					};
				},
			}}>
			<Grid container spacing={2}>
				{(type || title || subtitle) && (
					<Grid item xs={12}>
						<Grid container direction="column" alignItems={alignItems} spacing={0}>
							{type && (
								<Grid item>
									<IconCircle size="lg" type={type} />
								</Grid>
							)}
							{title && <Grid item>{renderTitle()}</Grid>}
							{subtitle && (
								<Grid item>
									<Typography
										color="brand.grey.500"
										fontWeight={600}
										variant="caption"
										{...subtitle}
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
				)}

				<Grid id="dialog-actions-description" item xs={12}>
					{children}
					{typeof content === 'string' ? <Typography>{content}</Typography> : content}
				</Grid>

				{actions.length > 0 && (
					<Grid item xs={12}>
						<Grid alignItems="center" container justifyContent="flex-end" spacing={2}>
							{actions.map((action, i) => {
								const { children, ...propsAction } = action;
								const Component = 'loading' in action ? LoadingButton : Button;
								return (
									<Grid item key={`dialog-action-btn-${i}`}>
										<Component {...propsAction}>{children}</Component>
									</Grid>
								);
							})}
						</Grid>
					</Grid>
				)}
			</Grid>
			{hasClose && (
				<IconButton
					aria-label="Close"
					color="inherit"
					onClick={onClose}
					size="small"
					sx={(theme) => {
						return {
							position: 'absolute',
							right: theme.spacing(1),
							top: theme.spacing(1),
						};
					}}>
					<IconClear />
				</IconButton>
			)}
		</Dialog>
	);
};
